import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CloudOff, Loader2, Share2 } from "lucide-react";
import AdminDesk from "../components/AdminDesk";
import AdminFloorGrid from "../components/AdminFloorGrid";
import AdminReferralsChrome from "../referrals/AdminReferralsChrome";
import AdminReferralsFiltersRail from "../referrals/AdminReferralsFiltersRail";
import ReferralsFloorIndex from "../referrals/floor/ReferralsFloorIndex";
import ReferralsFloorDetail from "../referrals/floor/ReferralsFloorDetail";
import { fetchAdminReferralDesk } from "../referrals/api/fetchAdminReferrals";
import { clientFilterReferrals, sortReferrals } from "../referrals/constants";
import "../styles/admin-markets.css";
import "../styles/admin-markets-floor.css";
import "../referrals/admin-referrals.css";

const PAGE_SIZE = 15;

export default function ReferralsPage() {
  const [desk, setDesk] = useState("all");
  const [sortKey, setSortKey] = useState("newest");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [referrals, setReferrals] = useState([]);
  const [topReferrers, setTopReferrers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 320);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async ({ pageNum = 1, refresh = false } = {}) => {
      setLoading(true);
      if (refresh) setRefreshing(true);
      setError(null);

      try {
        const data = await fetchAdminReferralDesk({
          page: pageNum,
          limit: PAGE_SIZE,
          desk,
          search: debouncedSearch,
          sort: sortKey,
          refresh,
        });

        setReferrals(data?.referrals || []);
        setTopReferrers(data?.topReferrers || []);
        setSummary(data?.summary || null);
        setPagination((prev) => ({
          ...prev,
          ...(data?.pagination || {}),
          limit: data?.pagination?.limit || PAGE_SIZE,
        }));
      } catch (err) {
        setReferrals([]);
        setError(err.response?.data?.error || err.message || "Failed to load referral desk");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [desk, debouncedSearch, sortKey]
  );

  const fetchPage = useCallback(
    (pageNum, refresh = false) => {
      setPage(pageNum);
      load({ pageNum, refresh });
    },
    [load]
  );

  useEffect(() => {
    fetchPage(1);
  }, [desk, debouncedSearch, sortKey, fetchPage]);

  const handleRefresh = () => load({ pageNum: page, refresh: true });

  const handlePageChange = (nextPage) => {
    if (nextPage === page || nextPage < 1) return;
    fetchPage(nextPage);
  };

  const displayReferrals = useMemo(() => {
    let list = clientFilterReferrals(referrals, search);
    return sortReferrals(list, sortKey);
  }, [referrals, search, sortKey]);

  const selectedReferral = useMemo(() => {
    if (!selectedId) return null;
    return (
      displayReferrals.find((item) => item.id === selectedId) ||
      referrals.find((item) => item.id === selectedId) ||
      null
    );
  }, [displayReferrals, referrals, selectedId]);

  useEffect(() => {
    if (!displayReferrals.length) {
      setSelectedId(null);
      return;
    }
    const stillVisible =
      selectedId && displayReferrals.some((item) => item.id === selectedId);
    if (!stillVisible) {
      setSelectedId(displayReferrals[0].id);
    }
  }, [displayReferrals, selectedId]);

  const activeFilterCount = [desk !== "all", sortKey !== "newest"].filter(Boolean).length;

  const handleClearFilters = () => {
    setDesk("all");
    setSortKey("newest");
    setSearch("");
  };

  const railProps = {
    desk,
    sortKey,
    summary,
    onDeskChange: setDesk,
    onSortChange: setSortKey,
    onClear: handleClearFilters,
    activeCount: activeFilterCount,
  };

  const isLoading = loading && !refreshing && referrals.length === 0;

  return (
    <div className="am-page arf-page">
      <div className="am-aurora" aria-hidden>
        <span className="am-aurora-a arf-aurora" />
      </div>

      <AdminReferralsChrome
        search={search}
        onSearchChange={setSearch}
        loading={loading || refreshing}
        onRefresh={handleRefresh}
        filtersOpen={filtersOpen}
        onFiltersToggle={() => setFiltersOpen((v) => !v)}
        activeFilterCount={activeFilterCount}
        summary={summary}
        totalLoaded={pagination.total || referrals.length}
      />

      <AdminDesk
        sectionKey="referrals"
        sidebarLabel="Referral filters"
        sidebar={<AdminReferralsFiltersRail {...railProps} />}
      >
        {error ? (
          <div className="arf-error-banner" role="alert">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)}>
              Dismiss
            </button>
          </div>
        ) : null}

        <div className="am-board am-board-floor">
          {isLoading ? (
            <div className="am-state">
              <Loader2 size={28} className="am-spin" />
              <p>Loading referrals…</p>
            </div>
          ) : error && !referrals.length ? (
            <div className="am-state">
              <CloudOff size={28} />
              <p>{error}</p>
              <button
                type="button"
                className="am-chrome-btn am-chrome-btn-accent"
                onClick={handleRefresh}
              >
                Try again
              </button>
            </div>
          ) : displayReferrals.length === 0 && !loading ? (
            <div className="am-state">
              <Share2 size={28} />
              <h2>No referrals match</h2>
              <p>
                {desk === "ready"
                  ? "No referrals are ready to pay right now."
                  : "Try clearing filters or adjusting your search."}
              </p>
              {activeFilterCount || search ? (
                <button
                  type="button"
                  className="am-chrome-btn am-chrome-btn-accent"
                  onClick={handleClearFilters}
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          ) : (
            <AdminFloorGrid
              sectionKey="referrals"
              index={
                <ReferralsFloorIndex
                  referrals={displayReferrals}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  desk={desk}
                  pagination={pagination}
                  loading={loading}
                  onPageChange={handlePageChange}
                />
              }
              detail={
                <ReferralsFloorDetail
                  referral={selectedReferral}
                  topReferrers={topReferrers}
                  summary={summary}
                />
              }
            />
          )}
        </div>
      </AdminDesk>

      {filtersOpen ? (
        <div
          className="am-filters-backdrop"
          onClick={() => setFiltersOpen(false)}
          role="presentation"
        >
          <div
            className="am-filters-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Referral filters"
          >
            <AdminReferralsFiltersRail
              {...railProps}
              compact
              onClose={() => setFiltersOpen(false)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
